import { useCallback } from 'react';

import { usePico } from '../pico';
import { type PaywallTrackingProps } from './use-paywall-tracking';

export type UpgradeButtonClickedProps = {
  name: string;
  location: string;
  targetUrl?: string;
};

/**
 * Hook for tracking upgrade button clicks.
 * Should be used when any upgrade button is clicked.
 */
export const useUpgradeTracking = () => {
  const pico = usePico();

  const trackUpgradeButtonClicked = useCallback(
    (props: UpgradeButtonClickedProps) => {
      if (pico === null) {
        return;
      }
      pico.track('upgrade_button_clicked', {
        button_name: props.name,
        button_location: props.location,
        target_url: props.targetUrl ?? null,
        page_url: typeof window !== 'undefined' ? window.location.href : null,
      });
    },
    [pico],
  );

  return {
    trackUpgradeButtonClicked,
  };
};

/**
 * Props for buildUpgradePlanUrl - paywallType is optional and defaults to 'page'
 */
export type BuildUpgradePlanUrlProps = Omit<PaywallTrackingProps, 'paywallType'> & {
  /**
   * Opt-in: URL to redirect the user to after a successful subscription
   * purchase or upgrade triggered from /upgrade-plan, instead of `/onboarding`.
   * Serialized as the `post_checkout_url` query param so /upgrade-plan can
   * re-thread it onto its outgoing checkout URLs. Callers are responsible for
   * validating with `isVimeoRedirectableUrl` before passing.
   * See `@vimeo/ui/libs/postCheckoutUrl`.
   */
  postCheckoutUrl?: string;
};

/**
 * Builds a URL to /upgrade-plan with paywall tracking parameters.
 * These parameters will be read by the upgrade-plan page to track `paywall_displayed`.
 */
export const buildUpgradePlanUrl = (
  paywallTracking: BuildUpgradePlanUrlProps,
  additionalParams?: Record<string, string>,
): string => {
  const params = new URLSearchParams({
    paywall_trigger: paywallTracking.paywallTrigger,
    paywall_location: paywallTracking.paywallLocation,
    paywall_type: 'page',
    paywall_feature: paywallTracking.paywallFeature,
    ...(paywallTracking.postCheckoutUrl
      ? { post_checkout_url: paywallTracking.postCheckoutUrl }
      : undefined),
    ...additionalParams,
  });

  return `/upgrade-plan?${params.toString()}`;
};
